import { computed, inject, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FeatureApi } from './feature.api';
import { IFeature } from './feature.model';

export type StoreTab = 'All' | 'Enabled' | string;

const MAX_RECOMMENDATIONS = 3;

@Injectable({ providedIn: 'root' })
export class FeatureCatalogStore {
  private readonly api = inject(FeatureApi);

  readonly activeTab = signal<StoreTab>('All');
  readonly searchQuery = signal('');

  private readonly catalog = rxResource({
    stream: () => this.api.getCatalog(),
  });

  readonly isLoading = computed(() => this.catalog.isLoading());
  readonly error = computed(() => this.catalog.error());

  private readonly allItems = computed<IFeature[]>(() => this.catalog.value()?.results ?? []);

  findById(id: number): IFeature | undefined {
    return this.allItems().find((f) => f.id === id);
  }

  findBySlug(slug: string): IFeature | undefined {
    return this.allItems().find((f) => f.slug === slug);
  }

  readonly categories = computed(() => {
    const seen = new Set<string>();
    const result: { id: number; name: string }[] = [];
    for (const f of this.allItems()) {
      if (!seen.has(f.category_name)) {
        seen.add(f.category_name);
        result.push({ id: f.category, name: f.category_name });
      }
    }
    return result.sort((a, b) => a.id - b.id);
  });

  readonly categoryProgress = computed(() =>
    this.categories().map((cat) => {
      const items = this.allItems().filter((f) => f.category_name === cat.name);
      return {
        ...cat,
        enabled: items.filter((f) => f.is_enabled).length,
        total: items.length,
      };
    }),
  );

  readonly allTabs = computed<StoreTab[]>(() => [
    'All',
    ...this.categories().map((c) => c.name),
    'Enabled',
  ]);

  readonly recommendedItems = computed<IFeature[]>(() =>
    this.allItems()
      .filter((f) => f.is_recommended && !f.is_enabled)
      .slice(0, MAX_RECOMMENDATIONS),
  );

  readonly filteredItems = computed<IFeature[]>(() => {
    const tab = this.activeTab();
    const query = this.searchQuery().toLowerCase().trim();
    let items = this.allItems();

    if (tab === 'Enabled') {
      items = items.filter((f) => f.is_enabled);
    } else if (tab !== 'All') {
      items = items.filter((f) => f.category_name === tab);
    }

    if (query) {
      items = items.filter(
        (f) => f.name.toLowerCase().includes(query) || f.summary.toLowerCase().includes(query),
      );
    }

    return items;
  });

  setTab(tab: StoreTab): void {
    this.activeTab.set(tab);
  }

  setSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }
}
